// zod schemas for the calendar event form (W40.1 F7).
//
// Mirrors `app/modules.calendar.schemas.CalendarEventCreate` so a client-side
// failure lands before the round trip. The date cap is pinned to
// `app.modules.calendar.models.MAX_RANGE_DAYS` via `CALENDAR_RANGE_DAYS` (the
// pair-test W39.72 added), because "最多 366 天" appearing in two places as a
// literal is how the two drift.

import { z } from "zod";

import { CALENDAR_RANGE_DAYS } from "@/lib/apiLimits";

const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "日期格式為 YYYY-MM-DD")
  .refine((s) => !Number.isNaN(Date.parse(`${s}T00:00:00Z`)), "日期不正確");

/** `leave | trip | cycle | manual` — the categories a person owns. */
export const calendarOwnedCategorySchema = z.enum(["leave", "trip", "cycle", "manual"]);

export const calendarEventFormSchema = z.object({
  title: z.string().min(1, "請輸入標題").max(100, "標題最長 100 字"),
  category: calendarOwnedCategorySchema,
  owner: z.enum(["me", "partner"]),
  visibility: z.enum(["shared", "private"]),
  /**
   * The selected days, ascending, de-duplicated.
   *
   * Not a range — a set. W40.1 F7 changed the mini calendar from drag-to-extend
   * to non-contiguous multi-select, and this schema is what makes that a real
   * change rather than a UI affordance: the form hands a `string[]` to the
   * submit path, which decides for itself whether the set is one interval (one
   * row) or N singletons (N rows).
   */
  days: z
    .array(isoDate)
    .min(1, "請至少選一天")
    .max(CALENDAR_RANGE_DAYS, `最多選 ${CALENDAR_RANGE_DAYS} 天`),
});

export type CalendarEventFormInput = z.input<typeof calendarEventFormSchema>;
export type CalendarEventFormParsed = z.output<typeof calendarEventFormSchema>;

/**
 * Is this set of days one unbroken interval?
 *
 * The whole of the create branch rests on this. True → one row with
 * `start_date` + `end_date` (Q15: a range is a single row, not an N-way
 * fan-out). False → N independent rows, which is the only thing the storage
 * model can express for 「10/5、10/9、10/20」.
 *
 * Exported so the test can pin the boundary directly rather than through the
 * submit path.
 */
export function isContiguous(days: readonly string[]): boolean {
  if (days.length <= 1) return true;
  const sorted = [...days].sort();
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = Date.parse(`${sorted[i - 1]}T00:00:00Z`);
    const next = Date.parse(`${sorted[i]}T00:00:00Z`);
    if (Number.isNaN(prev) || Number.isNaN(next)) return false;
    if (next - prev !== 86_400_000) return false;
  }
  return true;
}
